import type { PropsWithChildren } from 'react';
import { createElement, forwardRef } from 'react';
import { EMPTY_OBJECT } from '@frameio/utils';
import type { FeatureInput } from '../hooks/useFeatureGatePlanSuggestion';
import { useFeatureGatePlanSuggestion } from '../hooks/useFeatureGatePlanSuggestion';

export type DisableProps = PropsWithChildren<{
  /** The element type that wraps the content being disabled */
  as?: string;
  /** Styles that should show when the feature is disabled */
  disabledStyles?: React.CSSProperties;
  /** The feature we are checking */
  feature: FeatureInput;
  /** shouldOverrideFeatureGate: This is false by default. If the feature was
   * put into use when the account was on a higher tier plan, we can override this behavior to
   * give the user the chance to remove that setting.
   */
  shouldOverrideFeatureGate?: boolean;
}>;

function disableEvent(event: React.SyntheticEvent) {
  event.persist();
  event.preventDefault();
  event.stopPropagation();
  // still allow tabbing out of the element
  if ((event as React.KeyboardEvent).key === 'Tab') {
    (event.target as HTMLElement).blur();
  }
}

/**
 * Wraps a component to disable all of the children.
 * By default there is no visual indication that the children are disabled.
 * Pass in `disabledStyles` to style the disabled children.
 */
export const Disable = forwardRef(
  (
    {
      as = 'div',
      children,
      disabledStyles = EMPTY_OBJECT,
      feature,
      shouldOverrideFeatureGate = false,
    }: DisableProps,
    forwardedRef
  ) => {
    const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(feature);

    if (
      featureGatePlanSuggestion?.includedInCurrentPlan ||
      shouldOverrideFeatureGate
    )
      return children;

    return createElement(
      as,
      {
        onClick: disableEvent,
        onKeyDown: disableEvent,
        ref: forwardedRef,
        style: {
          pointerEvents: 'none',
          userSelect: 'none',
          ...disabledStyles,
        },
        tabIndex: -1,
      },
      children
    );
  }
);
